import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import map from 'lodash.map';
import sortBy from 'lodash.sortby';
import uniq from 'lodash.uniq';
import { ChevronRight, CircleAlert, Mail, Receipt, Star, TriangleAlert, Zap } from 'lucide-react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import numeral from 'numeral';
import { Component, createRef } from 'react';
import { flushSync } from 'react-dom';
import styled, { css } from 'styled-components';
import {
  deliveryKeyForBrand,
  getBreadcrumbItems,
  headingKeyForBrand,
  typeKeyForBrand,
} from '@/components/brand/brand-content';
import Card from '@/components/card';
import Image from '@/components/image';
import Loader from '@/components/loader';
import { Badge, Button as UiButton } from '@/components/ui';
import { sectionTitleStyle } from '@/components/ui/typography';
import { screens, up } from '@/themes';
import { flagForCountryCode, formatForCurrency, getFallbackImage, pricesForProduct } from '@/utils';
import { countryNameInSentence } from '@/utils/country-name';

const Select = dynamic(() => import('@/components/select'), { ssr: false });

dayjs.extend(duration);

/*
 * Styled-component names in this file are part of a contract: the dapp
 * (components/dapp/brand-details.js) restyles several of them by class name
 * (`[class*="brand__Content"]` and friends). Rename with care.
 */

// Shared by the amount and account-number inputs and the native select.
const fieldStyle = css`
  appearance: none;
  background-color: var(--card);
  border: 1px solid var(--input);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  color: var(--foreground);
  display: block;
  font-family: inherit;
  font-size: 16px;
  height: 48px;
  line-height: 24px;
  outline: none;
  padding: 0 16px;
  transition:
    border-color 150ms,
    box-shadow 150ms;
  width: 100%;

  &:focus-visible {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--ring) 30%, transparent);
  }
`;

const linkStyle = css`
  color: var(--primary);
  overflow-wrap: anywhere;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    text-decoration-thickness: 2px;
  }
`;

/*
 * Layout. One DOM, two arrangements.
 *
 * Below 1024px the blocks stack in a single column, in the order a buyer needs
 * them: breadcrumb, card (favourite, browse), name, country notice, warnings,
 * buy box, then Details and Terms as tabs. That is the DOM order too, with one
 * exception: the warnings live in the card column (Sidebar) and are moved down
 * to sit above the buy box with `order`. They are not focusable, so the focus
 * order still matches what is on screen.
 *
 * From 1024px there are two independent columns. Each block floats to its side
 * and clears the blocks above it there, so the columns stack on their own (a
 * grid would tie their row heights together). Left: card, browse, warnings,
 * redemption methods. Right: everything about buying, then Details and Terms as
 * tabs (same tab bar as mobile). A float cannot start above an earlier one,
 * which is why the warnings are not a block of their own between the notice
 * and the buy box: they would push the buy box down to their level. Redemption
 * methods render twice (sidebar + Details) with CSS picking one copy per
 * breakpoint, because a float cannot jump columns.
 */
const TWO_COLUMNS = up.lg;
const ONE_COLUMN = `@media (max-width: ${screens.lg - 1}px)`;

const leftColumn = css`
  min-width: 0;

  ${TWO_COLUMNS} {
    clear: left;
    float: left;
    width: calc(41.6667% - var(--brand-column-gap) / 2);
  }
`;

const rightColumn = css`
  min-width: 0;

  ${TWO_COLUMNS} {
    clear: right;
    float: right;
    width: calc(58.3333% - var(--brand-column-gap) / 2);
  }
`;

const Header = styled.header`
  ${rightColumn};
  margin-top: 20px;
  order: 3;

  ${TWO_COLUMNS} {
    margin-top: 0;
  }
`;

// Card column. On small screens it dissolves so its two parts can be ordered separately.
const Sidebar = styled.div`
  ${leftColumn};
  display: contents;

  ${TWO_COLUMNS} {
    display: block;
  }
`;

// The card and the actions under it. Not a full-width hero on tablets.
const Media = styled.div`
  max-width: 400px;
  min-width: 0;
  order: 2;
  width: 100%;

  ${TWO_COLUMNS} {
    max-width: none;
  }
`;

const Notice = styled.div`
  ${rightColumn};
  display: grid;
  gap: 12px;
  justify-items: start;
  margin-top: 16px;
  order: 4;
`;

const Notes = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
  min-width: 0;
  order: 5;

  /* Same width as the buy box it sits above. */
  ${ONE_COLUMN} {
    max-width: 520px;
  }
`;

const BrandLogo = styled(Card)`
  border: 1px solid var(--border);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  padding-bottom: 45%;

  > img,
  > picture,
  .lazyWrapper {
    border-radius: 0;
  }

  ${up.sm} {
    padding-bottom: 58%;
  }
`;

const ProductOptions = styled(Select)`
  margin-bottom: 0;

  .select {
    &__control {
      background-color: var(--card);
      border: 1px solid var(--input);
      border-radius: var(--radius-lg) !important;
      box-shadow: none !important;
      min-height: 48px;
      padding-left: 6px;

      &--is-focused {
        border-color: var(--ring);
      }

      &--menu-is-open {
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }
    }
  }
`;

const NativeProductSelect = styled.select`
  ${fieldStyle};
  /* A data URI cannot read a token: mid grey, legible on both themes. */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%238282a1' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5l5 5 5-5'/%3E%3C/svg%3E");
  background-position: right 16px center;
  background-repeat: no-repeat;
  cursor: pointer;
  padding-right: 40px;
`;

const Button = styled(UiButton).attrs({ $fullWidth: true, $size: 'pill', type: 'button' })`
  font-weight: 600;
  height: 48px;
`;

const Content = styled.section`
  ${rightColumn};
  margin-top: 16px;
  order: 6;
`;

const RadioOptions = styled.div`
  display: grid;
  gap: 8px;

  ${up.sm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const RadioOption = styled.label`
  align-items: flex-start;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  color: var(--foreground);
  cursor: pointer;
  display: flex;
  font-size: 15px;
  gap: 12px;
  line-height: 22px;
  min-width: 0;
  padding: 12px 14px;
  text-align: left;
  transition:
    background-color 150ms,
    border-color 150ms;

  input {
    appearance: none;
    background: var(--card);
    border: 1px solid var(--input);
    border-radius: 50%;
    box-sizing: border-box;
    flex-grow: 0;
    flex-shrink: 0;
    height: 18px;
    margin: 2px 0 0;
    transition: border 150ms;
    width: 18px;

    &:checked {
      border: 5px solid var(--primary);
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--ring) 30%, transparent);
    }
  }

  &:hover {
    border-color: color-mix(in oklab, var(--primary) 50%, transparent);
  }

  &.checked {
    background: color-mix(in oklab, var(--primary) 7%, transparent);
    border-color: color-mix(in oklab, var(--primary) 50%, transparent);
  }
`;

const BrandContainer = styled.div`
  --brand-column-gap: 48px;
  display: flex;
  flex-direction: column;
  padding: 24px 0 48px;

  h4 {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: normal;
    line-height: 20px;
    margin: 0 0 8px;
  }

  /* Phones: the purchase button follows the long list of options down the page. */
  @media (max-width: 639px) {
    ${RadioOptions} + ${Button} {
      bottom: 16px;
      box-shadow: var(--shadow-lg);
      position: sticky;
      transform: translate3d(0, 0, 0);
    }
  }

  ${TWO_COLUMNS} {
    display: flow-root;
    padding: 40px 0 64px;
  }

  ${up.xl} {
    --brand-column-gap: 64px;
  }
`;

const PurchaseButton = styled(Button)``;
PurchaseButton.displayName = 'PurchaseButton';

const BrandName = styled.h1`
  ${sectionTitleStyle};
  white-space: normal;
`;

const Summary = styled.p`
  color: var(--muted-foreground);
  font-size: 16px;
  line-height: 24px;
  margin: 8px 0 0;
  text-wrap: pretty;

  ${up.sm} {
    font-size: 18px;
    line-height: 28px;
  }
`;

const TopupContainer = styled.div`
  position: relative;
`;

const TopupInput = styled.input`
  ${fieldStyle};

  &::placeholder {
    color: var(--ink-subtle);
  }

  /* Invalid wins over the focus ring. With $lazy it waits until the field loses focus. */
  ${({ $invalid, $lazy }) =>
    $invalid &&
    css`
      &${$lazy ? ':not(:focus)' : ''},
      &${$lazy ? ':not(:focus)' : ':focus-visible'} {
        border-color: var(--destructive);
        box-shadow: 0 0 0 3px color-mix(in oklab, var(--destructive) 20%, transparent);
      }
    `};
`;

// Details and Terms share the space as tabs at every width.
const Tabs = styled.div`
  ${rightColumn};
  border-bottom: 1px solid var(--border);
  display: flex;
  gap: 4px;
  margin-top: 32px;
  order: 7;
`;

const SectionName = styled.button`
  appearance: none;
  background: none;
  border: 0;
  border-bottom: 2px solid ${({ $selected }) => ($selected ? 'var(--primary)' : 'transparent')};
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  color: ${({ $selected }) => ($selected ? 'var(--foreground)' : 'var(--muted-foreground)')};
  cursor: pointer;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: normal;
  line-height: 20px;
  margin: 0 0 -1px;
  padding: 12px;
  text-align: left;

  &:hover {
    color: var(--foreground);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: -2px;
  }
`;

/*
 * Details or Terms. With $tabbed it is a tab panel (hidden while the other tab
 * is selected; its heading is for screen readers only, since the tab says the
 * same). Alone it is a headed section. Both sit in the buying column under the
 * tab bar. The text is in the HTML once, whatever is showing.
 */
const Panel = styled.section`
  ${rightColumn};
  margin-top: ${({ $tabbed }) => ($tabbed ? '20px' : '32px')};
  order: 8;
  scroll-margin-top: calc(var(--nav-height) + 16px);

  ${({ $tabbed, $selected }) =>
    $tabbed &&
    !$selected &&
    css`
      display: none;
    `};

  /* No description: the first sub-heading sits right under the panel heading. */
  h2 + h3 {
    margin-top: 0;
  }

  ${TWO_COLUMNS} {
    margin-top: ${({ $tabbed }) => ($tabbed ? '20px' : '40px')};
  }
`;

const PanelTitle = styled.h2`
  color: var(--foreground);
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 28px;
  margin: 0 0 12px;

  ${({ $tabbed }) =>
    $tabbed &&
    css`
      clip-path: inset(50%);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    `};
`;

// Issuer-supplied text: shown as written, line breaks included. Only the type is set here.
const Description = styled.p`
  color: color-mix(in oklab, var(--foreground) 85%, transparent);
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  margin: 0;
  max-width: 620px;
  overflow-wrap: anywhere;
  text-align: left;
  white-space: pre-line;
`;

// Terms are fine print: the same treatment, one step smaller.
const Terms = styled(Description)`
  font-size: 15px;
  line-height: 24px;
`;

/*
 * Some issuers' descriptions and terms run long. Character count decides the
 * fold (no layout measuring), so server and client agree. The full text stays
 * in the HTML either way.
 */
const LONG_DESCRIPTION_LENGTH = 700;
const LONG_TERMS_LENGTH = 1800;
const DESCRIPTION_CLAMP_LINES = 8;

const DescriptionBody = styled.div`
  max-width: 620px;

  ${({ $folded }) =>
    $folded &&
    css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: ${DESCRIPTION_CLAMP_LINES};
      overflow: hidden;

      ${Description} {
        white-space: normal;
      }
    `};
`;

const FoldToggle = styled(UiButton).attrs({ $variant: 'link', type: 'button' })`
  height: auto;
  justify-content: flex-start;
  margin-top: 8px;
  padding: 0;
  white-space: normal;
`;

const TermsBody = styled.div`
  ${({ $folded }) =>
    $folded &&
    css`
      mask-image: linear-gradient(to bottom, black calc(100% - 96px), transparent);
      max-height: 480px;
      overflow: hidden;
    `};
`;

const Meta = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 16px;
`;

const metaLineStyle = css`
  align-items: flex-start;
  color: var(--muted-foreground);
  display: flex;
  font-size: 14px;
  gap: 8px;
  line-height: 20px;
  margin: 0;
  text-align: left;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const DeliveryType = styled.p`
  ${metaLineStyle};
`;

const TaxInfo = styled.p`
  ${metaLineStyle};
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SubHeader = styled.h3`
  color: var(--foreground);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: normal;
  line-height: 24px;
  margin: 28px 0 8px;
`;

/*
 * Redemption methods appear twice: in the card column on desktop, and inside
 * Details on mobile. `$showOnMobile` keeps a methods-only brand visible below
 * 1024px when there is no Details panel to host them. `$afterNotes` places
 * that mobile copy after the Important block when one exists.
 */
const RedemptionMethods = styled.div`
  display: none;
  margin-top: ${({ $placement }) => ($placement === 'sidebar' ? '16px' : '0')};

  ${({ $placement, $showOnMobile, $afterNotes }) =>
    $placement === 'sidebar'
      ? css`
          ${TWO_COLUMNS} {
            display: grid;
            gap: 8px;
          }

          ${$showOnMobile &&
          css`
            ${ONE_COLUMN} {
              display: grid;
              gap: 8px;
              /* Same band as Media (2) or Notes (5); DOM order finishes the stack. */
              order: ${$afterNotes ? 5 : 2};
            }
          `}
        `
      : css`
          ${ONE_COLUMN} {
            display: contents;
          }
        `};
`;

const RedemptionMethodsHeading = styled(SubHeader)`
  ${({ $placement }) =>
    $placement === 'sidebar' &&
    css`
      font-size: 14px;
      line-height: 20px;
      margin: 0;
    `};
`;

const URL = styled.a`
  ${linkStyle};
  display: inline-block;
  font-size: 16px;
  line-height: 24px;
  text-align: left;
`;

const RedemptionUrl = styled(URL)`
  margin-top: 12px;
`;

const OutOfStockMessage = styled.p`
  align-items: center;
  background: color-mix(in oklab, var(--destructive) 10%, transparent);
  border-radius: var(--radius-lg);
  color: var(--destructive);
  display: flex;
  font-size: 15px;
  font-weight: 500;
  gap: 8px;
  line-height: 22px;
  margin: 0;
  padding: 12px 16px;

  svg {
    flex-shrink: 0;
  }
`;

const OptionsWrapper = styled.section`
  background-color: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 520px;
  padding: 16px;

  ${up.sm} {
    padding: 20px;
  }
`;

const OptionDescription = styled.div`
  color: var(--muted-foreground);
  font-size: 13px;
  line-height: 1.5;

  span {
    display: block;
    margin-top: 4px;
  }
`;

const OptionDescriptionSmall = styled.div`
  font-size: 13px;
`;

const CustomAmountContainer = styled(TopupContainer)`
  align-items: center;
  display: flex;
`;

CustomAmountContainer.displayName = 'CustomAmountContainer';

const SwitchLine = styled.p`
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 20px;
  margin: 12px 0 0;
`;

const CustomAmountSwitch = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--primary);
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

CustomAmountSwitch.displayName = 'CustomAmountSwitch';

const SliderContainer = styled.div`
  flex: 1;
  min-width: 0;
  user-select: none;
`;

const PriceRange = styled.small`
  color: var(--muted-foreground);
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 4px 16px;
  line-height: 20px;
  margin-top: 8px;

  strong {
    font-weight: 600;
  }

  ${({ $amount, $increment, $max, $min }) => css`
    ${
      $amount < $min &&
      css`
        .min {
          color: var(--destructive);
        }
      `
    }

    ${
      $amount > $max &&
      css`
        .max {
          color: var(--destructive);
        }
      `
    }

    ${
      ($amount / 100) % $increment !== 0 &&
      css`
        .increment {
          color: var(--destructive);
        }
      `
    }
  `}
`;

const CountryWarning = styled.p`
  background: var(--muted);
  border-radius: var(--radius-lg);
  color: var(--foreground);
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  padding: 10px 14px;

  span {
    display: inline-block;
    margin-right: 2px;
  }
`;

const sanitizeText = (text) => {
  if (text && typeof text === 'string') {
    return text.replace(/ {2,}/gi, ' ');
  }
  return text;
};

const ImportantNotesHeader = styled.p`
  align-items: center;
  color: var(--foreground);
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  line-height: 20px;
  margin: 0 0 6px;

  svg {
    color: var(--notes-color);
    flex-shrink: 0;
  }
`;

const ImportantNotesContent = styled.p`
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  outline: none;
  overflow-wrap: anywhere;
  white-space: pre-line;

  @media (max-width: 639px) {
    mask-image: linear-gradient(to top, transparent, white);
    max-height: 40px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  strong {
    color: var(--foreground);
    font-weight: 600;
  }
`;

const ReadMore = styled.small`
  color: var(--primary);
  cursor: pointer;
  display: none;
  font-size: 14px;
  margin-top: 8px;
  text-decoration: underline;
  text-underline-offset: 3px;

  @media (max-width: 639px) {
    display: block;
  }
`;

const ImportantNotes = styled.div`
  --notes-color: ${({ $tone }) =>
    $tone === 'danger' ? 'var(--status-danger)' : 'var(--status-warning)'};
  background-color: var(--card);
  border: 1px solid var(--border);
  border-left: 4px solid var(--notes-color);
  border-radius: var(--radius-lg);
  padding: 12px 14px;

  &:focus {
    outline: none;

    ${ImportantNotesContent} {
      mask-image: none;
      max-height: unset;
    }

    ${ReadMore} {
      display: none;
    }
  }

  ${({ $expanded }) =>
    $expanded &&
    css`
      ${ImportantNotesContent} {
        mask-image: none;
        max-height: unset;
      }

      ${ReadMore} {
        display: none;
      }
    `}
`;

const LoadingState = styled.div`
  height: 140px;
  position: relative;
`;

const BreadCrumbContainer = styled.nav`
  ${rightColumn};
  order: 1;
  align-items: center;
  color: var(--muted-foreground);
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 4px;
  line-height: 20px;
  margin: 0 0 12px;

  a,
  a:visited,
  a:active,
  button {
    color: color-mix(in oklab, var(--foreground) 80%, transparent);
  }

  a:hover,
  button:hover {
    color: var(--primary);
  }

  button {
    appearance: none;
    background: none;
    border: 0;
    cursor: pointer;
    font: inherit;
    padding: 0;
  }

  svg {
    color: var(--ink-subtle);
    flex-shrink: 0;
  }
`;

const AddToCartContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const FavoriteButton = styled(UiButton).attrs({
  $fullWidth: true,
  $size: 'pill',
  $variant: 'outline',
  type: 'button',
})`
  margin-top: 12px;
`;

// A way back to the shop, under the card. Navigation, not the purchase action:
// text link with the same size and padding as the favourite control above it.
const BrowseButton = styled(UiButton).attrs({
  $fullWidth: true,
  $size: 'pill',
  $variant: 'link',
})`
  ${linkStyle};
  justify-content: center;
  margin-top: 4px;
  white-space: normal;

  && {
    background-color: transparent;
  }
`;

const Separator = () => <ChevronRight aria-hidden="true" size={16} strokeWidth={1.75} />;

const BreadCrumb = ({ brand, isDapp, label, onGoBack }) => {
  if (isDapp) {
    return (
      <BreadCrumbContainer aria-label={label}>
        <button type="button" onClick={onGoBack}>
          {brand.country.name}
        </button>
        <Separator />
        <span aria-current="page">{brand.name}</span>
      </BreadCrumbContainer>
    );
  }

  return (
    <BreadCrumbContainer aria-label={label}>
      {getBreadcrumbItems(brand).map((item) =>
        item.href ? (
          <span key={item.href} style={{ display: 'contents' }}>
            <Link href={item.href}>{item.name}</Link>
            <Separator />
          </span>
        ) : (
          <span aria-current="page" key={item.name}>
            {item.name}
          </span>
        )
      )}
    </BreadCrumbContainer>
  );
};

// The two information panels, in tab order. `name` is the value of `selectedTab`.
const DETAILS_PANEL_ID = 'details';

const TABS = [
  {
    name: 'description',
    label: 'detailsTab',
    tabId: 'brand-tab-details',
    panelId: DETAILS_PANEL_ID,
  },
  {
    name: 'disclaimer',
    label: 'termsTab',
    tabId: 'brand-tab-terms',
    panelId: 'brand-terms-panel',
  },
];

class Brand extends Component {
  isCustomAmount = false;
  selectedTab = 'description';
  descriptionExpanded = false;
  termsExpanded = false;

  //TODO: Handle strings in here
  get validAmount() {
    if (!this.props.store.product) return false;

    const {
      amount,
      product: {
        maxPriceInCents,
        minPriceInCents,
        variablePrice,
        allowedPricesInCents,
        currency,
        quoteCurrency,
        increment,
      },
    } = this.props.store;
    const value = parseInt(amount, 10);

    if (!variablePrice) {
      return (
        (!!allowedPricesInCents && allowedPricesInCents.includes(value)) ||
        minPriceInCents === value
      );
    } else {
      const currs = ['CAD', 'EUR', 'AUD', 'GBP'];
      let min = minPriceInCents;
      if (currency === quoteCurrency && currs.includes(currency)) {
        min = Math.max(minPriceInCents, 500);
      }
      return (
        !!value &&
        typeof value === 'number' &&
        !Number.isNaN(value) &&
        ((minPriceInCents && value >= min) || !minPriceInCents) &&
        ((maxPriceInCents && value <= maxPriceInCents) || !maxPriceInCents) &&
        (numeral(increment).value() > 1
          ? numeral(amount).divide(100).value() % numeral(increment).value() === 0
          : true)
      );
    }
  }

  constructor() {
    super();
    this.amountInputRef = createRef();
    this.tabRefs = { description: createRef(), disclaimer: createRef() };
    makeObservable(this, {
      selectedTab: observable,
      isCustomAmount: observable,
      descriptionExpanded: observable,
      termsExpanded: observable,
      validAmount: computed,
      selectTab: action,
      switchCustomAmount: action,
      toggleDescription: action,
      toggleTerms: action,
    });
  }

  componentDidMount() {
    const dappProvider = typeof window !== 'undefined' && (window.valora || window.bidaliProvider);

    if (dappProvider?.phoneNumber) {
      this.props.store.onChangeAccountNumber(`${dappProvider.phoneNumber}`);
    }

    document.addEventListener('click', this.onDetailsLinkClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDetailsLinkClick);
  }

  /*
   * Links to the Details panel (the FAQ's "details section" points at #details).
   * Below 1024px the panel can be behind the Terms tab, where the jump would do
   * nothing: select Details first, rendered before the browser follows the link.
   */
  onDetailsLinkClick = (event) => {
    const link = event.target?.closest?.(`a[href="#${DETAILS_PANEL_ID}"]`);

    if (link && this.selectedTab !== 'description') {
      flushSync(() => this.selectTab('description'));
    }
  };

  // Tab bar keyboard support: left / right arrows, Home and End move between tabs and select.
  onTabKeyDown = (event) => {
    const names = TABS.map((tab) => tab.name);
    const current = names.indexOf(this.selectedTab);
    let next = null;

    if (event.key === 'ArrowRight') {
      next = (current + 1) % names.length;
    } else if (event.key === 'ArrowLeft') {
      next = (current - 1 + names.length) % names.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = names.length - 1;
    }

    if (next === null) return;

    event.preventDefault();
    this.selectTab(names[next]);
    this.tabRefs[names[next]].current?.focus();
  };

  onSelectOption = (option) => {
    const { store } = this.props;
    const { product, value } = option;
    store.setProduct(product);
    store.setAmount(value);
  };

  onNativeSelectChange = (event) => {
    const { amountOptions } = this.getDerivedProductState();
    const option = amountOptions.find(
      (opt) => `${opt.product.id}:${opt.value}` === event.target.value
    );
    if (option) {
      this.onSelectOption(option);
    }
  };

  getPlainOptionLabel = (option) => {
    const { brand, products } = this.props;
    const { product } = option;

    if (option.value === -1) {
      return 'Custom Amount';
    }

    let label = formatForCurrency({ amount: option.value, currency: product.currency });
    if (product.name !== brand.name && products.length > 1) {
      label = `${label} - ${product.name}`;
    }
    return label;
  };

  onChangeCustomAmount = (amount) => {
    const { store } = this.props;
    const value = parseInt(amount, 10);

    if (!Number.isNaN(value)) {
      if (this.amountInputRef?.current) {
        this.amountInputRef.current.value = value;
      }

      store.setAmount(value * 100);
    } else {
      store.setAmount(0);
    }
  };

  //TODO: Handle strings in here
  setMinOrMax = (e) => {
    const { store } = this.props;
    const { product } = store;
    const value = parseInt(e.target.value, 10) * 100;
    const inputRef = this.amountInputRef?.current;
    const increment = numeral(product.increment).value();

    if (product?.minPriceInCents && value < product.minPriceInCents) {
      inputRef.value = product.minPriceInCents / 100;
      store.setAmount(product.minPriceInCents);
    } else if (product?.maxPriceInCents && value > product.maxPriceInCents) {
      inputRef.value = product.maxPriceInCents / 100;
      store.setAmount(product.maxPriceInCents);
    } else if (increment > 1 && (value / 100) % increment !== 0) {
      const closestMultiple = Math.round(value / 100 / increment) * increment;
      store.setAmount(closestMultiple * 100);
    }
  };

  hasLongDescriptions = (products) => {
    if (!products || products.length === 1) return false;

    const averageDescriptionLength = Math.round(
      products
        .map((p) => (p.description ? p.description.length : null))
        .reduce((acc, cur) => acc + cur, 0) / products.length
    );

    return averageDescriptionLength > 20;
  };

  formatOptionLabel = (option, selectContext) => {
    const { brand, products } = this.props;
    const { product, isProduct } = option;

    if (option.value === -1) {
      return 'Custom Amount';
    }

    if (product.additionalInfo) {
      const additionalInfo = product.additionalInfo.find(
        (info) => numeral(info.value).value() * 100 === numeral(option.value).value()
      );
      if (additionalInfo) {
        if (selectContext.context === 'value') {
          return (
            <div>
              <div>
                <strong>{`${formatForCurrency({ amount: option.value, currency: product.currency })}`}</strong>
              </div>
              <OptionDescriptionSmall>
                {additionalInfo.features.map((feature, index) => (
                  <span key={feature.description}>
                    {feature.description}: {feature.value}
                    {index < additionalInfo.features.length - 1 && ', '}
                  </span>
                ))}
              </OptionDescriptionSmall>
            </div>
          );
        }
        return (
          <div>
            <strong>{`${formatForCurrency({ amount: option.value, currency: product.currency })}`}</strong>
            <OptionDescription>
              {additionalInfo.features.map((feature) => (
                <span key={feature.description}>
                  <strong>{feature.description}:</strong> {feature.value}
                </span>
              ))}
            </OptionDescription>
          </div>
        );
      }
    }

    //TODO: Handle additionalInfo on the brand...find the item in the array that has a matching value
    // and show the `features` description / value
    if (isProduct) {
      if (
        selectContext.context === 'value' ||
        (!product.description && !product.validityPeriodIso)
      ) {
        if (product.name === brand.name) {
          return `${formatForCurrency({ amount: option.value, currency: product.currency })}`;
        }
        return product.name;
      }

      const label = product.name; //.replace(/.00$/, '');

      return (
        <div>
          <strong>{label}</strong>
          <OptionDescription>
            <span>{product.description}</span>
            <span>
              {product.validityPeriodIso && (
                <i>
                  {this.props.t('validFor', {
                    days: dayjs.duration(product.validityPeriodIso).asDays(),
                  })}
                </i>
              )}
            </span>
          </OptionDescription>
        </div>
      );
    }

    let label = `${product.name}`;

    // if (label.indexOf(`${product.currency} ${option.value / 100}`) === -1) {
    label = `${formatForCurrency({ amount: option.value, currency: product.currency })}`;

    if (product.name !== brand.name && products.length > 1) {
      label = `${label} - ${product.name}`;
    }
    // }

    return label;
  };

  switchCustomAmount = () => {
    const { products, store } = this.props;
    const [product] = products;
    //TODO: Handle strings in here
    const value = Math.max(product.minPriceInCents, 500);
    this.isCustomAmount = !this.isCustomAmount;

    if (this.isCustomAmount) {
      this.onSelectOption({ product, value });
    } else {
      store.setAmount(0);
    }
  };

  selectTab = (tabName) => {
    this.selectedTab = tabName;
  };

  toggleDescription = () => {
    this.descriptionExpanded = !this.descriptionExpanded;
  };

  toggleTerms = () => {
    this.termsExpanded = !this.termsExpanded;
  };

  renderRedemptionMethods = (
    placement,
    { afterNotes = false, showOnMobile = false, t } = {}
  ) => {
    const { brand } = this.props;
    const methods = brand.redemptionMethods;

    if (!methods || methods.length === 0) return null;

    return (
      <RedemptionMethods
        $afterNotes={afterNotes}
        $placement={placement}
        $showOnMobile={showOnMobile}
      >
        <RedemptionMethodsHeading $placement={placement}>
          {t('redemptionMethods')}
        </RedemptionMethodsHeading>
        <Tags>
          {methods.map((method) => (
            <Badge key={method} $variant="secondary">
              {method}
            </Badge>
          ))}
        </Tags>
      </RedemptionMethods>
    );
  };

  purchaseNow = async () => {
    const { isDapp, router, store } = this.props;
    const { accountNumber, amount, brand, product } = store;

    const added = await store.purchaseNow(accountNumber, amount, brand, product);

    if (added && !isDapp) {
      router.push('/checkout');
    }

    if (isDapp) {
      store.cart.setShowDappCheckout(true);
    }
  };

  getDerivedProductState() {
    const { products, store, brand } = this.props;
    const productId = store?.product?.id;
    const amount = store?.amount;
    const productsKey = products?.length && products.map((p) => p.id).join(',');
    const cacheKey = `${productsKey}-${productId}-${amount}`;
    if (this._derivedProductCache && this._derivedProductCache.key === cacheKey) {
      return this._derivedProductCache.value;
    }
    const productTypes = products ? uniq(map(products, 'type')) : [];
    const isGiftcard = productTypes.length === 1 && productTypes[0] === 'giftcard';
    const isTopup = productTypes.length === 1 && productTypes[0] === 'topup';
    const isESim = productTypes.length === 1 && productTypes[0] === 'esim';
    const isSingleProduct = products && products.length === 1;
    const allowsCustomAmount = isSingleProduct && products[0].variablePrice;
    const prices =
      isSingleProduct && pricesForProduct(products[0]) ? pricesForProduct(products[0]) : [];
    let amountOptions = [];
    let selectedOption = null;
    const mapProduct = (product) => {
      let value = product.minPriceInCents;
      if (product.allowedPricesInCents && product.allowedPricesInCents.length === 1) {
        value = product.allowedPricesInCents[0];
      }
      return { value, product, isProduct: true };
    };
    if (brand && products) {
      if (prices.length > 0) {
        amountOptions = amountOptions.concat(
          prices.map((amt) => ({ value: amt, product: products[0] }))
        );
      } else if (products.length > 0) {
        amountOptions = products.map(mapProduct).filter((option) => !!option.value);
        amountOptions = sortBy(amountOptions, 'value');
      }
      selectedOption = amountOptions.filter(
        (opt) => store.product && opt.product.id === store.product.id && opt.value === store.amount
      )[0];
    }
    const value = {
      productTypes,
      isGiftcard,
      isTopup,
      isESim,
      isSingleProduct,
      allowsCustomAmount,
      prices,
      amountOptions,
      selectedOption,
    };
    this._derivedProductCache = { key: cacheKey, value };
    return value;
  }

  render() {
    const {
      brand,
      products,
      store,
      isDapp,
      locale,
      // size,
      t,
    } = this.props;
    const countryName = countryNameInSentence(brand.country, locale);

    const {
      amount,
      addToCart,
      addToFavorites,
      removeFromFavorites,
      accountNumber,
      onChangeAccountNumber,
      hasValidAccountNumber,
    } = store;
    const hasLongDescriptions = this.hasLongDescriptions(products);
    const {
      isGiftcard,
      isTopup,
      isESim,
      allowsCustomAmount,
      prices,
      amountOptions,
      selectedOption,
    } = this.getDerivedProductState();
    const showCustomAmount = allowsCustomAmount && this.isCustomAmount;
    let deliveryType = null;
    let DeliveryIcon = Mail;
    let requiresAccountNumber = false;
    let accountNumberLabel = t('enterAccountNumber');

    let accountNumberInputType = 'text';
    const canPurchase =
      !!brand.products &&
      brand.enabled &&
      !brand.discontinued &&
      !brand.thirdPartyDisabled &&
      brand.country.enabled &&
      !brand.isOutOfStock;
    if (products && products.length > 0) {
      if (products[0].deliveryType === 'email') {
        deliveryType = t('emailVoucher');
        if (isESim) {
          deliveryType = t('emailESim');
          //TODO: eSims - requiresAccountNumber ONLY if they are topping up
          // accountNumberLabel = t('enterICCID');
        }
      } else if (products[0].deliveryType === 'phone') {
        requiresAccountNumber = true;
        accountNumberInputType = 'tel';
        deliveryType = t('instantDelivery');
        DeliveryIcon = Zap;
        accountNumberLabel = t('enterPrepaidNumber');
        if (isTopup) {
          deliveryType = t('instantDeliveryMinutes');
        }
      } else if (products[0].deliveryType === 'account') {
        requiresAccountNumber = true;
        deliveryType = t('instantDelivery');
        DeliveryIcon = Zap;
      }
    }

    const allowCart = !!addToCart && isGiftcard;
    const showPurchaseNow = !!this.purchaseNow && !requiresAccountNumber && !allowCart;

    const hasRedemptionMethods = !!brand.redemptionMethods && brand.redemptionMethods.length > 0;
    // Website lives in About on the storefront; the dapp has no About section.
    const hasDescription =
      !!brand.description ||
      !!brand.redemptionInstructions ||
      !!brand.redemptionInstructionsUrl ||
      (!!isDapp && !!brand.websiteUrl);
    const hasDisclaimer = brand.disclaimer && brand.disclaimer !== '';

    const formattedDescription = sanitizeText(brand.description);
    const formattedDisclaimer = sanitizeText(brand.disclaimer);
    const formattedRedemptionInstructions = sanitizeText(brand.redemptionInstructions);
    const taxInfo = products && products.length > 0 ? products[0].taxInfo : null;
    const hasNotes = !!brand.importantNotes || ['topup', 'data'].includes(brand.type);
    const hasBuyArea = !brand.products || canPurchase || !!taxInfo || !!deliveryType;
    // Tabs only when there are two panels to choose between; a lone panel just shows.
    const tabbed = hasDescription && !!hasDisclaimer;
    const hasLongDescription =
      !!formattedDescription && formattedDescription.length > LONG_DESCRIPTION_LENGTH;
    const hasLongTerms = !!hasDisclaimer && brand.disclaimer.length > LONG_TERMS_LENGTH;
    let activeTab = this.selectedTab;
    if (!tabbed) {
      activeTab = hasDescription ? 'description' : 'disclaimer';
    }
    const isFavorite = store.cart.favorites.some((favorite) => favorite.id === brand.id);

    const handleClickURL = (url) => (event) => {
      const { onClickURL } = this.props;
      event.preventDefault();

      if (typeof onClickURL === 'function') {
        return onClickURL(url);
      }

      if (url.startsWith('http') && typeof window !== 'undefined') {
        return window.open(url, '_blank');
      }

      return window.open(url);
    };

    return (
      <BrandContainer>
        <BreadCrumb
          brand={brand}
          isDapp={isDapp}
          label={t('breadcrumb')}
          onGoBack={this.props.onGoBack}
        />
        <Sidebar>
          <Media>
            <BrandLogo $backgroundColor={brand.backgroundColor}>
              <Image
                alt={t('logoAlt', { brandName: brand.name })}
                fallback={getFallbackImage(brand.type)}
                height={150}
                priority
                src={brand.imageUrl}
                unoptimized={true}
                width={250}
              />
            </BrandLogo>
            {isGiftcard && (
              <FavoriteButton
                aria-pressed={isFavorite}
                onClick={isFavorite ? removeFromFavorites : addToFavorites}
              >
                <Star fill={isFavorite ? 'currentColor' : 'none'} strokeWidth={1.75} />
                {isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
              </FavoriteButton>
            )}
            {!isDapp && (
              <BrowseButton href={`/buy/${brand.country.slug}`}>
                {t('browseMore', { type: typeKeyForBrand(brand) })}
              </BrowseButton>
            )}
          </Media>
          {hasNotes && (
            <Notes>
              {!!brand.importantNotes && (
                <ImportantNotes tabIndex="-1">
                  <ImportantNotesHeader>
                    <TriangleAlert aria-hidden="true" size={16} strokeWidth={1.75} />
                    {t('important')}
                  </ImportantNotesHeader>
                  <ImportantNotesContent>{brand.importantNotes}</ImportantNotesContent>
                  <ReadMore>{t('readMore')}</ReadMore>
                </ImportantNotes>
              )}
              {['topup', 'data'].includes(brand.type) && (
                <>
                  {brand.type === 'topup' && (
                    <ImportantNotes tabIndex="-1" $expanded>
                      <ImportantNotesHeader>
                        <TriangleAlert aria-hidden="true" size={16} strokeWidth={1.75} />
                        {t('important')}
                      </ImportantNotesHeader>
                      <ImportantNotesContent>{t('prepaidWarning')}</ImportantNotesContent>
                    </ImportantNotes>
                  )}
                  <ImportantNotes tabIndex="-1" $expanded $tone="danger">
                    <ImportantNotesHeader>
                      <CircleAlert aria-hidden="true" size={16} strokeWidth={1.75} />
                      {t('mobileMoney')}
                    </ImportantNotesHeader>
                    <ImportantNotesContent>
                      {t.rich('notMobileMoney', {
                        strong: (children) => <strong>{children}</strong>,
                      })}
                    </ImportantNotesContent>
                  </ImportantNotes>
                </>
              )}
            </Notes>
          )}
          {hasRedemptionMethods &&
            this.renderRedemptionMethods('sidebar', {
              afterNotes: hasNotes,
              showOnMobile: !hasDescription,
              t,
            })}
        </Sidebar>
        <Header>
          <BrandName>
            {t('heading', { type: headingKeyForBrand(brand), brandName: brand.name })}
          </BrandName>
          {!isDapp && (
            <Summary>
              {t('summary', {
                type: typeKeyForBrand(brand),
                delivery: deliveryKeyForBrand(brand),
                country: countryName,
              })}
            </Summary>
          )}
        </Header>
        <Notice>
          {(brand.isOutOfStock || brand.thirdPartyDisabled) && (
            <OutOfStockMessage>
              <CircleAlert aria-hidden="true" size={18} strokeWidth={1.75} />
              {t('outOfStock')}
            </OutOfStockMessage>
          )}
          <CountryWarning>
            <span>{flagForCountryCode(brand.country.id)}</span>{' '}
            {t('regionWarning', { country: countryName })}. <strong>{t('salesAreFinal')}</strong>
          </CountryWarning>
        </Notice>
        {hasBuyArea && (
          <Content>
            {!brand.products && (
              <LoadingState>
                <Loader />
              </LoadingState>
            )}
            {canPurchase && (
              <OptionsWrapper>
                {prices && !hasLongDescriptions && !showCustomAmount && (
                  <div>
                    {products.some((p) => p.additionalInfo?.length > 0) ? (
                      <ProductOptions
                        className="select"
                        aria-label={`${t('selectOption')}`}
                        isSearchable={false}
                        formatOptionLabel={this.formatOptionLabel}
                        options={amountOptions}
                        onChange={this.onSelectOption}
                        placeholder={`${t('selectOption')}`}
                        value={selectedOption}
                      />
                    ) : (
                      <NativeProductSelect
                        aria-label={`${t('selectOption')}`}
                        onChange={this.onNativeSelectChange}
                        value={
                          selectedOption
                            ? `${selectedOption.product.id}:${selectedOption.value}`
                            : ''
                        }
                      >
                        <option disabled value="">
                          {t('selectOption')}
                        </option>
                        {amountOptions.map((option) => (
                          <option
                            key={`${option.product.id}:${option.value}`}
                            value={`${option.product.id}:${option.value}`}
                          >
                            {this.getPlainOptionLabel(option)}
                          </option>
                        ))}
                      </NativeProductSelect>
                    )}
                    {allowsCustomAmount && (
                      <SwitchLine>
                        {t.rich('pickCustomAmount', {
                          switch: (children) => (
                            <CustomAmountSwitch type="button" onClick={this.switchCustomAmount}>
                              {children}
                            </CustomAmountSwitch>
                          ),
                        })}
                      </SwitchLine>
                    )}
                  </div>
                )}
                {showCustomAmount && (
                  <div>
                    <CustomAmountContainer>
                      <SliderContainer>
                        <TopupInput
                          required
                          ref={this.amountInputRef}
                          type="number"
                          //TODO: Handle strings in here
                          min={Math.max(products[0].minPriceInCents / 100, 5)}
                          max={products[0].maxPriceInCents / 100}
                          onBlur={this.setMinOrMax}
                          onChange={(e) => this.onChangeCustomAmount(e.target.value)}
                          step={
                            numeral(products[0].increment).value() <= 1
                              ? 1
                              : numeral(products[0].increment).value()
                          }
                          $invalid={!this.validAmount}
                          value={amount / 100}
                        />
                        <PriceRange
                          $amount={amount}
                          $increment={numeral(products[0].increment).value()}
                          $max={products[0].maxPriceInCents}
                          $min={Math.max(products[0].minPriceInCents, 500)}
                        >
                          {/* //TODO: Handle strings in here */}
                          <span className="min">
                            <strong>{t('min')}:</strong>{' '}
                            {formatForCurrency({
                              amount: Math.max(products[0].minPriceInCents, 500),
                              currency: products[0].currency,
                            })}
                          </span>
                          <span className="max">
                            <strong>{t('max')}:</strong>{' '}
                            {formatForCurrency({
                              amount: products[0].maxPriceInCents,
                              currency: products[0].currency,
                            })}
                          </span>
                          {numeral(products[0].increment).value() > 1 && (
                            <span className="increment">
                              <strong>{t('multiplesOf')}:</strong>{' '}
                              {formatForCurrency({
                                amount: numeral(products[0].increment).multiply(100).value(),
                                currency: products[0].currency,
                              })}
                            </span>
                          )}
                        </PriceRange>
                      </SliderContainer>
                    </CustomAmountContainer>
                    <SwitchLine>
                      {t.rich('pickFixedAmount', {
                        switch: (children) => (
                          <CustomAmountSwitch type="button" onClick={this.switchCustomAmount}>
                            {children}
                          </CustomAmountSwitch>
                        ),
                      })}
                    </SwitchLine>
                  </div>
                )}
                {allowCart && (
                  <AddToCartContainer>
                    <Button
                      disabled={!this.validAmount}
                      onClick={this.validAmount ? addToCart : null}
                    >
                      {t('addToCart')}
                    </Button>
                  </AddToCartContainer>
                )}
                {showPurchaseNow && (
                  <PurchaseButton
                    disabled={!this.validAmount}
                    onClick={this.validAmount ? this.purchaseNow : null}
                  >
                    {t('purchase')}
                  </PurchaseButton>
                )}
                {requiresAccountNumber && (
                  <div>
                    <h4>{accountNumberLabel}</h4>
                    <TopupContainer>
                      <TopupInput
                        required
                        placeholder={brand.exampleNumber || ''}
                        type={accountNumberInputType}
                        onChange={(e) => onChangeAccountNumber(e.target.value)}
                        $invalid={!!accountNumber && !hasValidAccountNumber}
                        $lazy
                        value={accountNumber}
                      />
                    </TopupContainer>
                  </div>
                )}
                {hasLongDescriptions && (
                  <>
                    <h4 style={{ margin: 0 }}>{t('selectOption')}</h4>
                    <RadioOptions>
                      {amountOptions.map((option) => (
                        <RadioOption
                          className={
                            store.product && store.product.id === option.product.id ? 'checked' : ''
                          }
                          key={option.product.id}
                        >
                          <input
                            checked={!!store.product && store.product.id === option.product.id}
                            value={option.product.id}
                            onChange={() => this.onSelectOption(option)}
                            type="radio"
                          />
                          {this.formatOptionLabel(option, {})}
                        </RadioOption>
                      ))}
                    </RadioOptions>
                  </>
                )}
                {requiresAccountNumber && (
                  <Button
                    disabled={!this.validAmount || !hasValidAccountNumber}
                    onClick={!this.validAmount || !hasValidAccountNumber ? null : this.purchaseNow}
                  >
                    {t('purchase')}
                  </Button>
                )}
              </OptionsWrapper>
            )}
            {(taxInfo || deliveryType) && (
              <Meta>
                {deliveryType && (
                  <DeliveryType>
                    <DeliveryIcon aria-hidden="true" size={16} strokeWidth={1.75} />
                    {deliveryType}
                  </DeliveryType>
                )}
                {taxInfo && (
                  <TaxInfo>
                    <Receipt aria-hidden="true" size={16} strokeWidth={1.75} />
                    {t('taxInfo', { rate: taxInfo.rate, name: taxInfo.name })}
                  </TaxInfo>
                )}
              </Meta>
            )}
          </Content>
        )}
        {tabbed && (
          <Tabs aria-label={t('infoTabs')} role="tablist">
            {TABS.map((tab) => (
              <SectionName
                aria-controls={tab.panelId}
                aria-selected={activeTab === tab.name}
                className={activeTab === tab.name ? 'selected' : ''}
                id={tab.tabId}
                key={tab.name}
                onClick={() => this.selectTab(tab.name)}
                onKeyDown={this.onTabKeyDown}
                ref={this.tabRefs[tab.name]}
                role="tab"
                $selected={activeTab === tab.name}
                tabIndex={activeTab === tab.name ? 0 : -1}
                type="button"
              >
                {t(tab.label)}
              </SectionName>
            ))}
          </Tabs>
        )}
        {hasDescription && (
          <Panel
            aria-labelledby="brand-details-heading"
            data-active={activeTab === 'description'}
            id={DETAILS_PANEL_ID}
            role={tabbed ? 'tabpanel' : undefined}
            $selected={activeTab === 'description'}
            $tabbed={tabbed}
          >
            <PanelTitle id="brand-details-heading" $tabbed={tabbed}>
              {t('detailsTab')}
            </PanelTitle>
            {!!formattedDescription && (
              <>
                <DescriptionBody
                  id="brand-description-text"
                  $folded={hasLongDescription && !this.descriptionExpanded}
                >
                  <Description>{formattedDescription}</Description>
                </DescriptionBody>
                {hasLongDescription && (
                  <FoldToggle
                    aria-controls="brand-description-text"
                    aria-expanded={this.descriptionExpanded}
                    onClick={this.toggleDescription}
                  >
                    {this.descriptionExpanded ? t('showLess') : t('readMore')}
                  </FoldToggle>
                )}
              </>
            )}

            {!!isDapp && !!brand.websiteUrl && (
              <>
                <SubHeader>{t('website')}</SubHeader>
                <URL
                  onClick={handleClickURL(brand.websiteUrl)}
                  href={brand.websiteUrl}
                  title={brand.name}
                  rel="noopener nofollow"
                  target="_blank"
                >
                  {brand.websiteUrl}
                </URL>
              </>
            )}

            {hasRedemptionMethods && this.renderRedemptionMethods('details', { t })}

            {(!!brand.redemptionInstructionsUrl || !!brand.redemptionInstructions) && (
              <>
                <SubHeader>{t('redemptionInstructions')}</SubHeader>
                {!!formattedRedemptionInstructions && (
                  <Description>{formattedRedemptionInstructions}</Description>
                )}
                {!!brand.redemptionInstructionsUrl && (
                  <RedemptionUrl
                    onClick={handleClickURL(brand.redemptionInstructionsUrl)}
                    title={`${brand.name} redemption instructions`}
                    href="#"
                    rel="noopener nofollow"
                    target="_blank"
                  >
                    {brand.redemptionInstructionsUrl}
                  </RedemptionUrl>
                )}
              </>
            )}
          </Panel>
        )}
        {hasDisclaimer && (
          <Panel
            aria-labelledby="brand-terms-heading"
            data-active={activeTab === 'disclaimer'}
            id="brand-terms-panel"
            role={tabbed ? 'tabpanel' : undefined}
            $selected={activeTab === 'disclaimer'}
            $tabbed={tabbed}
          >
            <PanelTitle id="brand-terms-heading" $tabbed={tabbed}>
              {t('termsTab')}
            </PanelTitle>
            <TermsBody id="brand-terms-text" $folded={hasLongTerms && !this.termsExpanded}>
              <Terms>{formattedDisclaimer}</Terms>
            </TermsBody>
            {hasLongTerms && (
              <FoldToggle
                aria-controls="brand-terms-text"
                aria-expanded={this.termsExpanded}
                onClick={this.toggleTerms}
              >
                {this.termsExpanded ? t('showLess') : t('readMore')}
              </FoldToggle>
            )}
          </Panel>
        )}
      </BrandContainer>
    );
  }
}

const BrandComponent = observer(Brand);

const HOC = (props) => {
  const t = useTranslations('brand');
  const { locale } = useRouter();

  return <BrandComponent {...props} locale={locale} t={t} />;
};

export default HOC;
